<script setup>
// CheckInSheet — canonical redemption-validation sheet, reusable across pass
// layouts. Orchestrator for the 8 check-in methods:
//
//   gps / pin / gps_or_pin / quiz / qr(intro) / gps_and_* → bottom sheet
//   qr (scanner) + gps_and_qr step 2 → full-screen black QR takeover
//
// On success → emits `success`; the parent then opens its reward flow.
//
// Port of the source `_shared/CheckInSheet.tsx` (React/Lovable). The source
// portals into a PhoneFrame overlay root; v4 has no PhoneFrame, so this
// teleports to <body> and replicates the source's bottom-sheet slide-up and
// QR takeover pixel-for-pixel in scoped SCSS (documented pixel-perfect
// exception to the Vuetify-first overlay recipe). Because the overlay lives
// outside the pass layout's DOM, the --fr-* theme vars are re-bound here from
// the `theme` prop (CSS custom properties don't cascade into teleports).
//
// Props:
//   open  — parent owns the open state; close is one-way via the `close` emit.
//   spec  — CheckInSpec (see ./checkin.js for the shape).
//   theme — { surface?, ink?, muted?, accent?, primary?, primaryForeground?,
//             fontDisplay?, fontSerifDisplay? } hex/font values mapped onto
//             the --fr-* custom properties.
import { ref, computed, watch, onUnmounted } from 'vue'
import { hasDetailsContent } from './checkin'
import { isIntercomAvailable, openIntercom } from '@/utils/intercom'
import SheetHeader from './SheetHeader.vue'
import DetailsPanel from './DetailsPanel.vue'
import DetailsView from './DetailsView.vue'
import GpsBody from './GpsBody.vue'
import PinBody from './PinBody.vue'
import GpsOrPinBody from './GpsOrPinBody.vue'
import QuizBody from './QuizBody.vue'
import QrIntroBody from './QrIntroBody.vue'
import GpsAndBody from './GpsAndBody.vue'
import QrTakeover from './QrTakeover.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  spec: { type: Object, required: true },
  theme: { type: Object, default: () => ({}) },
  // Debug hook threaded to GpsBody / GpsOrPinBody / GpsAndBody / QrTakeover:
  // 'denied' | 'unavailable' | 'timeout' | 'outside' | 'camera-denied'.
  forceState: { type: String, default: null },
  // Program-config override for the failure-recovery copy (see recoveryCopy.js),
  // threaded to the GPS bodies. Backend (ENG-2205) supplies it; null = platform defaults.
  recoveryCopy: { type: Object, default: null },
  // Live validation handler: async (params) => { ok, message }. Threaded to
  // every method body so THE SERVER decides every verdict (params.validate
  // marks the combo GPS pre-step). Absent = mock demo (local outcomes).
  submit: { type: Function, default: null },
})

// `help-fallback` fires when help was requested but Intercom isn't available,
// so the host layout can offer its own support affordance.
const emit = defineEmits(['close', 'success', 'help-fallback'])

// Tracks whether the GPS step of a gps_and_* combo has passed.
const gpsPassed = ref(false)
// Coords that passed the combo's GPS pre-step — folded into the final
// combined submission (the server re-validates BOTH factors together).
const gpsCoords = ref(null)
// When true, the sheet body is replaced with the check-in details view.
const detailsOpen = ref(false)
// When true, the full-screen QR scanner takeover is shown (for method=="qr"
// or for gps_and_qr step 2). QR always starts on the normal sheet with an
// "Open scanner" CTA so users get the same context as other methods first.
const qrScannerOpen = ref(false)
// Staff-override PIN path: set when a failure-recovery panel's "Use venue PIN
// instead" fires for a method whose spec carries a pin but has no PIN body of
// its own (plain gps with an override pin, or the QR takeover) — the sheet
// then renders PinBody in place of the method body.
const pinOverride = ref(false)
// True while the Intercom-unavailable fallback notice is shown.
const helpNotice = ref(false)

// Backdrop-dismiss safety: only close when a real press starts and ends on
// the backdrop after the sheet has been visible long enough. This prevents
// the original CTA tap (or a delayed compatibility mouse/click event from a
// touch tap) from landing on the freshly-mounted backdrop and immediately
// dismissing the sheet.
let openedAt = 0
let pressStartedOnBackdrop = false

watch(
  () => [props.open, props.spec?.method],
  () => {
    if (!props.open) {
      gpsPassed.value = false
      gpsCoords.value = null
      detailsOpen.value = false
      qrScannerOpen.value = false
      pinOverride.value = false
      helpNotice.value = false
    }
    if (props.open) openedAt = Date.now()
  },
  // Source's useEffect also runs on mount — keeps the backdrop-dismiss
  // safety window when the sheet mounts already open.
  { immediate: true },
)

const isCombo = computed(() =>
  ['gps_and_pin', 'gps_and_qr', 'gps_and_quiz'].includes(props.spec?.method),
)

// Full-screen QR takeover: shown once the user opens the scanner from the
// sheet (plain qr), or after GPS passes in a gps_and_qr combo. Suppressed
// while the PIN-override path is active.
const showQrTakeover = computed(
  () =>
    !pinOverride.value &&
    ((props.spec?.method === 'qr' && qrScannerOpen.value) ||
      (props.spec?.method === 'gps_and_qr' && gpsPassed.value)),
)

const qrSpec = computed(() =>
  props.spec?.method === 'qr'
    ? props.spec
    : {
        method: 'qr',
        venueName: props.spec?.venueName,
        instructions: props.spec?.instructions,
        // Staff-override pin passthrough — enables "Use venue PIN instead"
        // in the takeover's camera-denied recovery panel.
        pin: props.spec?.pin,
        details: props.spec?.details,
      },
)

// Spec for the staff-override PinBody.
const overridePinSpec = computed(() => ({
  method: 'pin',
  venueName: props.spec?.venueName,
  pin: props.spec?.pin,
  pinLength: props.spec?.pinLength,
  hint: props.spec?.hint,
  details: props.spec?.details,
}))

// Header reflects the body actually shown (PIN while overriding).
const headerSpec = computed(() => (pinOverride.value ? overridePinSpec.value : props.spec))

const onUsePinOverride = () => {
  qrScannerOpen.value = false
  pinOverride.value = true
}

// Help affordance: open Intercom when the widget is live; otherwise notify
// the host layout and show a small non-blocking inline notice.
let helpNoticeTimer = null

const onHelp = () => {
  if (isIntercomAvailable()) {
    openIntercom()
    return
  }
  emit('help-fallback')
  helpNotice.value = true
  clearTimeout(helpNoticeTimer)
  helpNoticeTimer = setTimeout(() => {
    helpNotice.value = false
  }, 6000)
}

onUnmounted(() => {
  clearTimeout(helpNoticeTimer)
})

// The takeover's submit folds the combo's GPS coords into the scanned code
// (gps_and_qr); plain qr submits the code alone.
const qrSubmit = computed(() => {
  if (!props.submit) return null
  const submit = props.submit
  if (props.spec?.method !== 'gps_and_qr') return submit
  return (params) => submit({ ...(gpsCoords.value || {}), ...params })
})

const onGpsPassed = (coords) => {
  gpsCoords.value = coords || null
  gpsPassed.value = true
}

async function confirmNoValidation() {
  const res = await props.submit?.({})
  if (res?.ok) emit('success')
}

// Plain qr: back returns to the intro sheet. gps_and_qr: no back — X closes all.
const qrCanGoBack = computed(() => props.spec?.method === 'qr')

const onTakeoverClose = () => {
  if (props.spec?.method === 'qr') {
    qrScannerOpen.value = false
  } else {
    emit('close')
  }
}

const onTakeoverBack = () => {
  qrScannerOpen.value = false
}

// Details view has content only when the spec carries details.
const canShowDetails = computed(() => hasDetailsContent(props.spec?.details))

// --fr-* theme layer re-bound on each teleported overlay root. Defaults match
// useFrTokens' fallbacks so an unthemed sheet still renders sensibly.
const themeVars = computed(() => {
  const t = props.theme || {}
  return {
    '--fr-surface': t.surface || '#ffffff',
    '--fr-ink': t.ink || '#1c1c1e',
    '--fr-muted': t.muted || '#6b7280',
    '--fr-accent': t.accent || t.primary || '#232F3E',
    '--fr-primary': t.primary || '#FF9900',
    '--fr-primary-foreground': t.primaryForeground || '#232F3E',
    '--fr-font-display': t.fontDisplay || 'inherit',
    '--fr-font-serif-display': t.fontSerifDisplay || "Georgia, 'Times New Roman', serif",
  }
})

const canDismissFromBackdrop = () => Date.now() - openedAt >= 650

const onBackdropPointerDown = (e) => {
  // Only count as backdrop press if the pointer landed on the backdrop
  // itself (not on the sheet content that stops propagation).
  pressStartedOnBackdrop = e.target === e.currentTarget && canDismissFromBackdrop()
}

const onBackdropPointerUp = (e) => {
  const startedHere = pressStartedOnBackdrop
  pressStartedOnBackdrop = false
  if (!startedHere || e.target !== e.currentTarget || !canDismissFromBackdrop()) return
  emit('close')
}

const swallowBackdropClick = (e) => {
  // Click is retained only as a no-op sink for browser-generated mouse
  // compatibility events after touch; pointerup owns actual dismissal.
  e.preventDefault()
  e.stopPropagation()
}
</script>

<template>
  <Teleport to="body">
    <QrTakeover
      v-if="open && showQrTakeover"
      :spec="qrSpec"
      :can-go-back="qrCanGoBack"
      :force-state="forceState"
      :help-notice="helpNotice"
      :submit="qrSubmit"
      :style="themeVars"
      @close="onTakeoverClose"
      @back="onTakeoverBack"
      @success="$emit('success')"
      @use-pin="onUsePinOverride"
      @help="onHelp"
    />

    <div
      v-else-if="open"
      class="cs-overlay"
      data-overlay
      :style="themeVars"
      @pointerdown="onBackdropPointerDown"
      @pointerup="onBackdropPointerUp"
      @click="swallowBackdropClick"
    >
      <div class="cs-overlay__backdrop" />
      <div class="cs-overlay__sheet" @click.stop @pointerdown.stop @pointerup.stop>
        <SheetHeader
          :spec="headerSpec"
          :back-mode="detailsOpen"
          @close="$emit('close')"
          @back="detailsOpen = false"
          @help="onHelp"
        />
        <div class="cs-overlay__body" data-scrollable>
          <div v-if="helpNotice" class="cs-overlay__help-notice">
            Support chat is unavailable right now — please contact your pass provider.
          </div>
          <DetailsView v-if="detailsOpen && canShowDetails" :details="spec.details" />
          <template v-else>
            <DetailsPanel :details="spec.details" @open="detailsOpen = true" />
            <!-- Staff-override PIN entry (from a recovery panel's "Use venue PIN instead"). -->
            <PinBody v-if="pinOverride" :spec="overridePinSpec" :submit="submit" @success="$emit('success')" />
            <GpsBody
              v-else-if="spec.method === 'gps'"
              :spec="spec"
              :force-state="forceState"
              :recovery-copy="recoveryCopy"
              :submit="submit"
              @success="$emit('success')"
              @use-pin="onUsePinOverride"
              @help="onHelp"
            />
            <PinBody v-else-if="spec.method === 'pin'" :spec="spec" :submit="submit" @success="$emit('success')" />
            <GpsOrPinBody
              v-else-if="spec.method === 'gps_or_pin'"
              :spec="spec"
              :force-state="forceState"
              :recovery-copy="recoveryCopy"
              :submit="submit"
              @success="$emit('success')"
              @help="onHelp"
            />
            <QuizBody v-else-if="spec.method === 'quiz'" :spec="spec" :submit="submit" @success="$emit('success')" @help="onHelp" />
            <QrIntroBody v-else-if="spec.method === 'qr'" :spec="spec" @open-scanner="qrScannerOpen = true" />
            <GpsAndBody
              v-else-if="isCombo"
              :spec="spec"
              :gps-passed="gpsPassed"
              :force-state="forceState"
              :recovery-copy="recoveryCopy"
              :submit="submit"
              @gps-passed="onGpsPassed"
              @success="$emit('success')"
              @help="onHelp"
            />
            <div v-else-if="!spec.method" class="cs-none">
              <p class="cs-none__text">Confirm your check-in<template v-if="spec.venueName"> at {{ spec.venueName }}</template>.</p>
              <button type="button" class="cs-none__cta gtm-click" @click="confirmNoValidation">Check in</button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

.cs-none {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cs-none__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}
.cs-none__cta {
  @include cs-cta;
}

// Source: `pointer-events-auto absolute inset-0 z-[170] flex items-end`.
// v4: fixed to the viewport (no PhoneFrame); the sheet column is constrained
// to the app's max width so it lines up with the pass on wide screens.
.cs-overlay {
  position: fixed;
  inset: 0;
  // Above Vuetify overlays/chrome; source's z-[170] is phone-frame-local.
  z-index: 2400;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  pointer-events: auto;

  // Source: `absolute inset-0 bg-black/55 backdrop-blur-[2px] animate-in
  //          fade-in duration-200`. pointer-events none so backdrop presses
  //          register on the overlay wrapper (which owns dismissal).
  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(2px);
    animation: cs-fade-in-kf 200ms both;
    pointer-events: none;
  }

  // Source: `relative flex max-h-[85%] w-full flex-col rounded-t-2xl
  //          ${theme.surface} shadow-2xl animate-in slide-in-from-bottom duration-300`
  &__sheet {
    position: relative;
    display: flex;
    max-height: 85%;
    width: 100%;
    max-width: var(--project-max-width, 480px);
    flex-direction: column;
    border-radius: 16px 16px 0 0; // rounded-t-2xl
    background: var(--fr-surface, #ffffff);
    color: var(--fr-ink, #1c1c1e);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); // shadow-2xl
    animation: cs-slide-up-kf 300ms both; // slide-in-from-bottom duration-300
  }

  // Tiny non-blocking notice when Intercom isn't available.
  &__help-notice {
    margin-bottom: 8px; // space-y-2 rhythm
    border-radius: 12px;
    background: cs-current(5%);
    padding: 8px 12px;
    text-align: center;
    font-size: 11px;
    color: var(--fr-muted, #6b7280);
  }

  // Source: `min-h-0 flex-1 overflow-y-auto no-scrollbar px-4 pt-2 pb-2`
  &__body {
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    padding: 8px 16px; // pt-2 px-4 pb-2

    // no-scrollbar
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

// tailwindcss-animate `animate-in fade-in duration-200`
@keyframes cs-fade-in-kf {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// tailwindcss-animate `animate-in slide-in-from-bottom duration-300`
@keyframes cs-slide-up-kf {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
